import { Button, ResponsiveTable, useTranslation } from '@hbs-us/ui';
import { Trash2 } from 'lucide-react';

import type { MoneyFormatters } from '../../_money/useMoneyFormatters.js';

/**
 * Editable invoice line-items table + discount / tax controls + a live totals
 * preview (the create/edit counterpart to the read-only invoice detail table).
 * Add/remove rows; each row edits description, quantity, unit, and unit price
 * with a read-only computed line total.
 *
 * The totals preview is DISPLAY ONLY — the server recomputes them authoritatively
 * via the shared `calculateInvoiceTotals` helper (the client cannot forge money).
 * The preview mirrors that server rule exactly: subtotal − discount → taxable
 * subtotal → +tax → total.
 *
 * Token-only styling (no hex — BRAND-06 / lint:no-hex); every control is
 * keyboard-operable with a visible or `aria-label`-ed label (A11Y-01).
 *
 * `ResponsiveTable` reflows each row to a card of labelled inputs below `md`, so a
 * phone types into full-width fields instead of a sideways-scrolling row. The
 * discount / tax controls and the totals preview below it already stack. `md`+ is
 * unchanged.
 */

export type InvoiceDiscountType = 'none' | 'percent' | 'amount';

export interface InvoiceLineRow {
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
}

export function newInvoiceLineRow(): InvoiceLineRow {
  return { description: '', quantity: '', unit: '', unitPrice: '' };
}

function numeric(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function roundQuantity(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

/** Line total for a row (unit price × quantity), mirrors the server `line_total`. */
export function invoiceRowLineTotal(row: InvoiceLineRow): number {
  return roundMoney(roundMoney(numeric(row.unitPrice)) * roundQuantity(numeric(row.quantity)));
}

export interface InvoiceTotalsPreview {
  subtotal: number;
  discountAmount: number;
  taxableSubtotal: number;
  tax: number;
  total: number;
}

/** Client-side totals preview — mirrors the server `calculateInvoiceTotals`. */
export function computeInvoiceTotals(
  rows: InvoiceLineRow[],
  discountType: InvoiceDiscountType,
  discountValue: string,
  taxRatePercent: string,
): InvoiceTotalsPreview {
  const subtotal = roundMoney(rows.reduce((sum, row) => sum + invoiceRowLineTotal(row), 0));
  const rawDiscount = roundMoney(numeric(discountValue));
  let discountAmount = 0;
  if (discountType === 'percent') discountAmount = roundMoney(subtotal * (rawDiscount / 100));
  else if (discountType === 'amount') discountAmount = roundMoney(Math.min(rawDiscount, subtotal));
  const taxableSubtotal = roundMoney(Math.max(subtotal - discountAmount, 0));
  const taxRate = roundMoney(numeric(taxRatePercent));
  const tax = roundMoney(taxableSubtotal * (taxRate / 100));
  return {
    subtotal,
    discountAmount,
    taxableSubtotal,
    tax,
    total: roundMoney(taxableSubtotal + tax),
  };
}

export interface InvoiceDraftPayloadSource {
  jobId: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  termsText: string;
  publicNotes: string;
  internalNotes: string;
  discountType: InvoiceDiscountType;
  discountValue: string;
  taxRatePercent: string;
  lineItems: InvoiceLineRow[];
}

export function buildInvoiceDraftPayload(form: InvoiceDraftPayloadSource) {
  return {
    jobId: form.jobId ? Number(form.jobId) : null,
    invoiceNumber: form.invoiceNumber || null,
    issueDate: form.issueDate,
    dueDate: form.dueDate,
    customerName: form.customerName,
    customerEmail: form.customerEmail || null,
    customerPhone: form.customerPhone || null,
    customerAddress: form.customerAddress || null,
    termsText: form.termsText || null,
    publicNotes: form.publicNotes || null,
    internalNotes: form.internalNotes || null,
    discountType: form.discountType,
    discountValue: numeric(form.discountValue),
    taxRate: numeric(form.taxRatePercent),
    lineItems: form.lineItems.map((row) => ({
      description: row.description,
      quantity: numeric(row.quantity),
      unit: row.unit || null,
      unitPrice: numeric(row.unitPrice),
    })),
  };
}

const INPUT_CLASS =
  'h-10 w-full rounded-md border border-input bg-background px-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background';
const NUMERIC_TD = 'px-2 py-2 text-right font-mono text-foreground text-sm';

export function InvoiceLineItemsEditor({
  rows,
  onRowsChange,
  discountType,
  discountValue,
  taxRatePercent,
  onDiscountTypeChange,
  onDiscountValueChange,
  onTaxRateChange,
  fmt,
}: {
  rows: InvoiceLineRow[];
  onRowsChange: (rows: InvoiceLineRow[]) => void;
  discountType: InvoiceDiscountType;
  discountValue: string;
  taxRatePercent: string;
  onDiscountTypeChange: (value: InvoiceDiscountType) => void;
  onDiscountValueChange: (value: string) => void;
  onTaxRateChange: (value: string) => void;
  fmt: MoneyFormatters;
}) {
  const { t } = useTranslation();
  const totals = computeInvoiceTotals(rows, discountType, discountValue, taxRatePercent);

  const updateRow = (index: number, patch: Partial<InvoiceLineRow>) => {
    onRowsChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const removeRow = (index: number) => {
    onRowsChange(rows.filter((_, i) => i !== index));
  };
  const addRow = () => {
    onRowsChange([...rows, newInvoiceLineRow()]);
  };

  return (
    <div className="flex flex-col gap-4">
      <ResponsiveTable>
        <table className="w-full border-collapse">
          <caption className="sr-only">{t('invoices:lineItems.caption')}</caption>
          <thead>
            <tr className="border-border border-b">
              <th
                scope="col"
                className="px-2 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                {t('invoices:lineItems.description')}
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                {t('invoices:lineItems.quantity')}
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                {t('invoices:lineItems.unit')}
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                {t('invoices:lineItems.unitPrice')}
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                {t('invoices:lineItems.lineTotal')}
              </th>
              <th
                scope="col"
                className="px-2 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide"
              >
                <span className="sr-only">{t('invoices:lineItemsEditor.rowActions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              // Rows are index-keyed: this is a positional editor (add/remove) with
              // no stable id until persisted, so the index IS the identity here.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional editor rows
              <tr key={index} className="border-border/60 border-b last:border-b-0">
                <td className="px-2 py-2">
                  <input
                    type="text"
                    aria-label={t('invoices:lineItemsEditor.descriptionFor', { n: index + 1 })}
                    value={row.description}
                    onChange={(e) => updateRow(index, { description: e.target.value })}
                    className={INPUT_CLASS}
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    inputMode="decimal"
                    aria-label={t('invoices:lineItemsEditor.quantityFor', { n: index + 1 })}
                    value={row.quantity}
                    onChange={(e) => updateRow(index, { quantity: e.target.value })}
                    className={`${INPUT_CLASS} text-right font-mono`}
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="text"
                    aria-label={t('invoices:lineItemsEditor.unitFor', { n: index + 1 })}
                    value={row.unit}
                    onChange={(e) => updateRow(index, { unit: e.target.value })}
                    className={INPUT_CLASS}
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    aria-label={t('invoices:lineItemsEditor.unitPriceFor', { n: index + 1 })}
                    value={row.unitPrice}
                    onChange={(e) => updateRow(index, { unitPrice: e.target.value })}
                    className={`${INPUT_CLASS} text-right font-mono`}
                  />
                </td>
                <td className={`${NUMERIC_TD} font-medium`}>
                  {fmt.formatCurrency(invoiceRowLineTotal(row))}
                </td>
                <td className="px-2 py-2 text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(index)}
                    disabled={rows.length <= 1}
                    aria-label={t('invoices:lineItemsEditor.removeRow', { n: index + 1 })}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ResponsiveTable>

      <div>
        <Button type="button" variant="secondary" onClick={addRow}>
          {t('invoices:lineItemsEditor.addRow')}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <span className="font-medium text-foreground text-sm">
            {t('invoices:form.discountHeading')}
          </span>
          <div className="flex gap-2">
            <select
              aria-label={t('invoices:form.discountType')}
              value={discountType}
              onChange={(e) => onDiscountTypeChange(e.target.value as InvoiceDiscountType)}
              className="h-10 rounded-md border border-input bg-background px-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            >
              <option value="none">{t('invoices:form.discountNone')}</option>
              <option value="percent">{t('invoices:form.discountPercent')}</option>
              <option value="amount">{t('invoices:form.discountAmount')}</option>
            </select>
            <input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              aria-label={t('invoices:form.discountValue')}
              value={discountValue}
              disabled={discountType === 'none'}
              onChange={(e) => onDiscountValueChange(e.target.value)}
              className={`${INPUT_CLASS} text-right font-mono disabled:opacity-50`}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-medium text-foreground text-sm">{t('invoices:form.taxRate')}</span>
          <input
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            aria-label={t('invoices:form.taxRate')}
            value={taxRatePercent}
            onChange={(e) => onTaxRateChange(e.target.value)}
            className={`${INPUT_CLASS} text-right font-mono`}
          />
        </div>
      </div>

      <dl className="ml-auto w-full max-w-xs space-y-1 text-sm">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">{t('invoices:form.subtotalLabel')}</dt>
          <dd className="font-mono text-foreground">{fmt.formatCurrency(totals.subtotal)}</dd>
        </div>
        {totals.discountAmount > 0 ? (
          <div className="flex justify-between">
            <dt className="text-muted-foreground">{t('invoices:form.discountLabel')}</dt>
            <dd className="font-mono text-foreground">
              -{fmt.formatCurrency(totals.discountAmount)}
            </dd>
          </div>
        ) : null}
        <div className="flex justify-between">
          <dt className="text-muted-foreground">{t('invoices:form.taxLabel')}</dt>
          <dd className="font-mono text-foreground">{fmt.formatCurrency(totals.tax)}</dd>
        </div>
        <div className="flex justify-between border-border border-t pt-1">
          <dt className="font-semibold text-foreground">{t('invoices:form.totalLabel')}</dt>
          <dd className="font-mono font-semibold text-foreground">
            {fmt.formatCurrency(totals.total)}
          </dd>
        </div>
      </dl>
    </div>
  );
}
